/**
 * Lot A — Présentation onglet Fidélité boutique (fiche client Admin).
 * Lecture seule ; catalogue F2/F2.5 via analytics.catalogueBoutique.
 * FIX-1 : nature d’effet = valeurPourcent/valeurEuros (pas un auto-libellé stale).
 */

export const BOUTIQUE_EMPTY_REWARDS =
  'Aucune récompense active pour le moment';

const AUTO_LIBELLE_POURCENT_RE = /^(\d+(?:[.,]\d+)?)\s*%\s*de\s+réduction$/i;
const AUTO_LIBELLE_EUR_RE = /^(\d+(?:[.,]\d+)?)\s*€\s*offerts$/i;

export function isAutoGeneratedLibelle(libelle) {
  if (typeof libelle !== 'string') return false;
  const t = libelle.trim();
  if (!t) return false;
  return AUTO_LIBELLE_POURCENT_RE.test(t) || AUTO_LIBELLE_EUR_RE.test(t);
}

function formatEffectNumber(n) {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 100) / 100;
  let s = String(rounded);
  if (s.includes('.')) {
    s = s.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
  }
  return s.replace('.', ',');
}

/**
 * Layout boutique uniquement si commerce clairement ciblé
 * (catalogueBoutique peut être vide / en échec de charge — pas de fallback Clickorico).
 */
export function isBoutiqueLoyaltyLayout(analytics) {
  return Boolean(analytics && analytics.boutiqueCommercantId);
}

function formatBoutiqueNatureTitle(reward) {
  if (!reward || typeof reward !== 'object') return 'Récompense';
  if (reward.effectType === 'REDUCTION_POURCENT') {
    const p = Number(reward.valeurPourcent);
    if (Number.isFinite(p) && p > 0) {
      return `${formatEffectNumber(p)} % de réduction`;
    }
    return 'Réduction en %';
  }
  if (reward.effectType === 'REDUCTION_EUR') {
    const e = Number(reward.valeurEuros);
    if (Number.isFinite(e) && e > 0) {
      return `${formatEffectNumber(e)} € offerts`;
    }
    return 'Réduction en euros';
  }
  return 'Récompense';
}

export function formatBoutiqueRewardTitle(reward) {
  if (!reward || typeof reward !== 'object') return 'Récompense';
  const nature = formatBoutiqueNatureTitle(reward);
  const lib =
    typeof reward.libelle === 'string' && reward.libelle.trim()
      ? reward.libelle.trim()
      : '';
  if (lib && !isAutoGeneratedLibelle(lib)) return lib;
  return nature;
}

export function formatBoutiqueRewardCout(reward) {
  const n = Number(reward?.coutPoints);
  if (!Number.isFinite(n) || n < 0) return '—';
  const rounded = Math.round(n);
  return rounded === 1 ? '1 point' : `${rounded} points`;
}

export function formatBoutiqueRewardStatus(reward) {
  if (!reward) return { accessible: false, label: '—' };
  if (reward.accessible) {
    return { accessible: true, label: 'Disponible' };
  }
  const missing = Number(reward.pointsManquants) || 0;
  return {
    accessible: false,
    label: missing > 0 ? `Encore ${missing} points` : 'Indisponible',
  };
}

/**
 * Cartes catalogue — toutes les ACTIVE exposées, sans « prochaine » unique.
 */
export function toBoutiqueRewardCards(catalogueBoutique) {
  const rows = Array.isArray(catalogueBoutique?.recompenses)
    ? catalogueBoutique.recompenses
    : [];
  return rows.map((r, index) => {
    const status = formatBoutiqueRewardStatus(r);
    return {
      key: r.id || r.rewardId || `reward-${index}`,
      title: formatBoutiqueRewardTitle(r),
      coutLabel: formatBoutiqueRewardCout(r),
      statusLabel: status.label,
      accessible: status.accessible,
      effectType: r.effectType || null,
    };
  });
}

/**
 * Ligne historique : préfère presentation API, fallback local minimal.
 */
export function toHistoryRowView(transaction) {
  const p = transaction?.presentation;
  if (p && typeof p === 'object') {
    return {
      id: transaction.id,
      credit: !!p.credit,
      pointsLabel: p.pointsLabel || `${p.credit ? '+' : '−'}${p.pointsAbs || 0} points`,
      title: p.title || null,
      subtitle: p.subtitle || null,
      secondaryId: p.secondaryId || null,
      secondaryNumero: p.secondaryNumero || null,
    };
  }
  const type = transaction?.type;
  const pts = Math.abs(Number(transaction?.points) || 0);
  const credit = type === 'GAIN' || type === 'BONUS';
  const date = transaction?.date
    ? new Date(transaction.date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;
  return {
    id: transaction?.id,
    credit,
    pointsLabel: `${credit ? '+' : '−'}${pts} points`,
    title: null,
    subtitle: date ? `Commande du ${date}` : null,
    secondaryId: null,
    secondaryNumero: null,
  };
}

/** Garde-fou : tokens interdits dans le layout boutique rendu. */
export const BOUTIQUE_FORBIDDEN_UI_TOKENS = [
  'Programme de fidélité Clickorico',
  'Platinum',
  'pointsPourReduction',
  'valeurReduction',
  'NiveauCarteFidelite',
];
